import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateService } from 'i18n';
import { merge, Subscription, timer } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

interface Breadcrumb {
  label: string;
  url: string;
}

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <nav aria-label="breadcrumb">
      <ol class="items">
        <li class="item" *ngFor="let crumb of breadcrumbs(); let last = last; let first = first">
          <a [routerLink]="crumb.url" *ngIf="!last">{{ crumb.label }}</a>
          <span *ngIf="last">{{ crumb.label }}</span>
        </li>
      </ol>
    </nav>
  `,
  styles: [`
    .items {
      display: flex;
      list-style: none;
      padding: 0;
      margin: 0;
    }
    .item {
      display: flex;
      align-items: center;
    }
    .item:not(:last-child)::after {
      content: '\\25B6';
      margin: 0 8px;
      color: #6c757d;
      font-size: 0.7em;
    }
    .item a {
      color: #0880c0;
      text-decoration: none;
    }
    .item a:hover {
      text-decoration: underline;
    }
    .item span {
      color: #6c757d;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  breadcrumbs = signal<Breadcrumb[]>([]);
  private subscription: Subscription = new Subscription();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      merge(
        timer(100), // Initial load with a 100ms delay
        this.translateService.langChanges.pipe(debounceTime(100)),
        this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
        this.translateService.events.pipe(filter(e => e.type === 'translationLoadSuccess'))
      ).subscribe(() => {
        this.buildBreadcrumbs();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private buildBreadcrumbs(): void {
    const crumbs: Breadcrumb[] = [];
    
    let currentRoute: ActivatedRoute | null = this.activatedRoute.root;
    const routeStack: { route: ActivatedRoute; url: string }[] = [];
    let currentUrl = '';

    while (currentRoute) {
      const snapshot = currentRoute.snapshot;
      const routeData = snapshot.data;
      const titleKey = routeData?.['title'] || '';

      if (!snapshot.routeConfig?.redirectTo && snapshot.url.length > 0) {
        const urlSegments = snapshot.url.map(segment => segment.path).join('/');
        currentUrl = currentUrl ? `${currentUrl}/${urlSegments}` : `/${urlSegments}`;
        if (titleKey) {
          routeStack.push({ route: currentRoute, url: currentUrl });
        }
      }

      currentRoute = currentRoute.firstChild;
    }

    routeStack.forEach(({ route, url }) => {
      const snapshot = route.snapshot;
      const routeData = snapshot.data;
      const titleKey = routeData?.['title'] || '';
      if (!titleKey) return;
      // Transloco answers a MISSING key with the key itself, so `|| titleKey` never
      // fired and an unlisted route rendered a raw "menu.Community Search" into the
      // page header. Compare against the key to fall back to the plain title.
      const menuKey = `menu.${titleKey}`;
      const translated = this.translateService.translate(menuKey);
      const translatedLabel = translated && translated !== menuKey ? translated : titleKey;
      const cleanUrl = url.split('/path:')[0];
      crumbs.push({
        label: translatedLabel,
        url: cleanUrl
      });
    });

    // handle dynamic 'path:' segments (e.g., /media/area/path:folderMediaId.folderMediaId/mediaId)
    const url = this.router.url;
    const pathIndex = url.indexOf('path:');
    if (pathIndex !== -1) {
      const baseUrl = url.substring(0, pathIndex)
      const pathPart = url.substring(pathIndex + 5).split('/')[0]; // remove tailing /mediaId 
      const folders = pathPart.split('.').filter(folder => folder && folder.trim() !== '');
      let currentPath = baseUrl;
      folders.forEach((folder, index) => {
        // only include 'path:' for the first segment
        if (index === 0) {
          currentPath += `path:${folder}`;
        } else {
          currentPath += `/${folder}`;
        }
        crumbs.push({
          label: folder,
          url: currentPath
        });
      });
    }
    this.breadcrumbs.set(crumbs);
  }
}